import { Head, Link, useForm, usePage } from '@inertiajs/react';
import { useEffect, useRef, useState, type FormEvent } from 'react';

import { AutoTextarea } from '@/components/admin/forms/AutoTextarea';
import { CoverImageField } from '@/components/admin/forms/CoverImageField';
import { MarkdownEditor } from '@/components/admin/forms/MarkdownEditor';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { trackUmamiEvent } from '@/lib/analytics';

export interface NewsPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  body: string;
  category: string | null;
  publishedAt: string | null;
  coverImageUrl: string | null;
}

interface NewsEditorProps {
  post: NewsPost | null;
  categories: string[];
  [key: string]: unknown;
}

const labelClass = 'text-on-surface mb-1.5 block text-sm font-medium';
const panelLabel = 'text-on-surface-light/70 mb-3 text-[10px] font-bold tracking-[0.1em] uppercase';

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

// Derived publication state from the chosen date: future → scheduled,
// past/now → published, empty → draft.
function publishState(value: string): { label: string; icon: string; className: string } {
  if (!value) {
    return {
      label: 'Sem data',
      icon: 'edit',
      className: 'text-on-surface-light border-line-strong bg-background',
    };
  }
  if (new Date(value).getTime() > Date.now()) {
    return {
      label: 'Agendada',
      icon: 'schedule',
      className: 'text-primary border-primary/40 bg-primary/8',
    };
  }
  return {
    label: 'Publicada',
    icon: 'check_circle',
    className: 'text-tertiary border-tertiary/40 bg-tertiary/10',
  };
}

export default function NewsEditor() {
  const { post, categories } = usePage<NewsEditorProps>().props;
  const isEdit = Boolean(post);

  const categoryOptions = [
    { label: '— sem categoria —', value: '' },
    ...categories.map((c) => ({ label: c, value: c })),
  ];

  const [slugManuallyEdited, setSlugManuallyEdited] = useState(isEdit);
  const [coverPreview, setCoverPreview] = useState<string | null>(post?.coverImageUrl ?? null);

  const form = useForm({
    news_post: {
      title: post?.title ?? '',
      slug: post?.slug ?? '',
      excerpt: post?.excerpt ?? '',
      body: post?.body ?? '',
      category: post?.category ?? '',
      published_at: post?.publishedAt ? post.publishedAt.slice(0, 16) : '',
    },
    cover_image: null as File | null,
  });
  const { data, setData, processing } = form;
  // Inertia types nested errors awkwardly; flatten to dotted/plain keys.
  const e = form.errors as Record<string, string | undefined>;

  form.transform((d) => {
    const newsPost = { ...d.news_post, category: d.news_post.category || null };
    const out: Record<string, unknown> = { news_post: newsPost };
    if (d.cover_image) out.cover_image = d.cover_image;
    return out;
  });

  // Auto-slug from title while creating, until the slug is edited by hand.
  const prevTitle = useRef(data.news_post.title);
  useEffect(() => {
    if (slugManuallyEdited) return;
    if (data.news_post.title === prevTitle.current) return;
    prevTitle.current = data.news_post.title;
    setData('news_post', { ...data.news_post, slug: slugify(data.news_post.title) });
  }, [data.news_post.title]); // eslint-disable-line react-hooks/exhaustive-deps

  function setField(key: keyof typeof data.news_post, value: string) {
    setData('news_post', { ...data.news_post, [key]: value });
  }

  function handleCover(file: File | null) {
    setData('cover_image', file);
    setCoverPreview(file ? URL.createObjectURL(file) : (post?.coverImageUrl ?? null));
  }

  function regenerateSlug() {
    setSlugManuallyEdited(false);
    setField('slug', slugify(data.news_post.title));
  }

  function submit(ev: FormEvent) {
    ev.preventDefault();
    const opts = { forceFormData: !!data.cover_image };
    trackUmamiEvent(post ? 'admin_content_update' : 'admin_content_create', {
      content_type: 'news',
    });
    if (post) form.patch(`/news/${post.id}`, opts);
    else form.post('/news', opts);
  }

  const state = publishState(data.news_post.published_at);
  const crumb = data.news_post.title.trim() || (isEdit ? 'Sem título' : 'Rascunho sem título');

  return (
    <>
      <Head title={isEdit ? 'Editar notícia — Administração' : 'Nova notícia — Administração'} />

      <form onSubmit={submit}>
        {/* Top bar */}
        <div className="border-line-strong bg-surface flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 shadow-[0_14px_34px_-28px_rgba(33,29,24,0.5)]">
          <Link
            href="/news"
            className="text-on-surface-light hover:text-primary inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"
          >
            <span className="material-symbols-outlined text-[18px]">arrow_back</span>
            Notícias
          </Link>
          <div className="border-line flex flex-col border-l pl-3.5 leading-tight">
            <span className="text-secondary text-[10px] font-bold tracking-[0.12em] uppercase">
              {isEdit ? 'Editar notícia' : 'Nova notícia'}
            </span>
            <span className="font-headline text-on-surface max-w-[40ch] truncate text-base font-semibold">
              {crumb}
            </span>
          </div>
          <div className="ml-auto flex items-center gap-2.5">
            <Link
              href="/news"
              className="bg-surface border-line-strong text-on-surface hover:border-primary hover:text-primary inline-flex items-center justify-center gap-2 rounded-md border px-4 py-2 text-sm font-semibold transition-colors duration-200"
            >
              Cancelar
            </Link>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={processing}
              loading={processing}
            >
              {!processing && <span className="material-symbols-outlined text-[18px]">save</span>}
              {processing ? 'A guardar…' : 'Guardar'}
            </Button>
          </div>
        </div>

        {/* Two-zone editor */}
        <div className="mt-5 grid grid-cols-1 items-start gap-5 lg:grid-cols-[1fr_320px]">
          {/* Writing column */}
          <div className="space-y-4">
            <div className="surface-card px-5 py-4">
              <input
                type="text"
                value={data.news_post.title}
                onChange={(ev) => setField('title', ev.target.value)}
                required
                placeholder="Título da notícia"
                aria-label="Título"
                className="font-headline text-on-surface placeholder:text-on-surface-light/40 w-full border-0 bg-transparent text-2xl leading-tight font-semibold outline-none sm:text-3xl"
              />
              <div className="text-on-surface-light/80 mt-2.5 flex flex-wrap items-center gap-x-1 gap-y-1 text-[13px]">
                <span className="text-on-surface-light/60">/noticias/</span>
                <input
                  type="text"
                  value={data.news_post.slug}
                  onChange={(ev) => {
                    setSlugManuallyEdited(true);
                    setField('slug', ev.target.value);
                  }}
                  required
                  aria-label="Slug"
                  placeholder="slug-da-noticia"
                  className="border-line-strong text-secondary focus:border-primary min-w-[14rem] flex-1 border-0 border-b border-dashed bg-transparent px-0.5 py-0.5 outline-none"
                />
                <button
                  type="button"
                  onClick={regenerateSlug}
                  className="text-on-surface-light hover:text-primary ml-1 inline-flex items-center gap-1 text-xs font-semibold transition-colors"
                >
                  <span className="material-symbols-outlined text-[15px]">autorenew</span>
                  regenerar
                </button>
              </div>
              {e.title && <p className="text-error mt-1.5 text-xs">{e.title}</p>}
              {e.slug && <p className="text-error mt-1 text-xs">{e.slug}</p>}
            </div>

            <div className="surface-card px-5 py-4">
              <label className={labelClass} htmlFor="news-excerpt">
                Resumo
              </label>
              <AutoTextarea
                id="news-excerpt"
                value={data.news_post.excerpt ?? ''}
                onChange={(ev) => setField('excerpt', ev.target.value)}
                rows={2}
                placeholder="Breve resumo da notícia (aparece nos cartões e na partilha)…"
              />
              {e.excerpt && <p className="text-error mt-1 text-xs">{e.excerpt}</p>}
            </div>

            <div className="surface-card px-5 py-4">
              <label className={labelClass} htmlFor="news-body">
                Conteúdo <span className="text-error">*</span>
              </label>
              <MarkdownEditor
                id="news-body"
                value={data.news_post.body}
                onChange={(v) => setField('body', v)}
                required
                rows={14}
                placeholder="Conteúdo da notícia em Markdown…"
              />
              {e.body && <p className="text-error mt-1 text-xs">{e.body}</p>}
            </div>
          </div>

          {/* Metadata aside */}
          <aside className="space-y-4 lg:sticky lg:top-4">
            <div className="surface-card px-4 py-4">
              <div className="mb-3 flex items-center justify-between">
                <span className={`${panelLabel} mb-0`}>Publicação</span>
                <span
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase ${state.className}`}
                >
                  <span className="material-symbols-outlined text-[13px]">{state.icon}</span>
                  {state.label}
                </span>
              </div>

              <div className="mb-3.5">
                <span className={labelClass}>Categoria</span>
                <Select
                  options={categoryOptions}
                  value={data.news_post.category ?? ''}
                  onChange={(v) => setField('category', v)}
                  placeholder="— sem categoria —"
                  className="w-full"
                />
                {e.category && <p className="text-error mt-1 text-xs">{e.category}</p>}
              </div>

              <div>
                <label className={labelClass} htmlFor="news-published">
                  Data de publicação
                </label>
                <input
                  id="news-published"
                  type="datetime-local"
                  value={data.news_post.published_at}
                  onChange={(ev) => setField('published_at', ev.target.value)}
                  className="bg-surface border-line text-on-surface focus:border-primary w-full rounded-md border px-3 py-2 text-sm transition-all outline-none focus:shadow-[0_0_0_3px_rgb(from_var(--color-primary)_r_g_b/0.12)]"
                />
                <p className="text-on-surface-light/70 mt-1.5 text-[11px]">
                  No futuro → agendada; no passado → publicada.
                </p>
                {e.published_at && <p className="text-error mt-1 text-xs">{e.published_at}</p>}
              </div>
            </div>

            <div className="surface-card px-4 py-4">
              <span className={panelLabel}>Imagem de capa</span>
              <CoverImageField
                previewUrl={coverPreview}
                onSelect={handleCover}
                onRemove={data.cover_image ? () => handleCover(null) : undefined}
              />
              {e.cover_image && <p className="text-error mt-1 text-xs">{e.cover_image}</p>}
            </div>
          </aside>
        </div>
      </form>
    </>
  );
}
